/* Gunnaro — global.js
   Drawers (native <dialog>), mega menu, sticky header, store hours, quantity steppers, toast.
   No dependencies. Every feature degrades to plain HTML without JS. */
(() => {
  const G = (window.Gunnaro = window.Gunnaro || {});

  /* ---------- Drawers ---------- */
  const lockScroll = () => document.body.classList.toggle('is-locked', !!document.querySelector('dialog.drawer[open]'));

  G.openDrawer = (id, opener) => {
    const dialog = document.getElementById(id);
    if (!dialog || dialog.open) return;
    dialog._opener = opener || document.activeElement;
    dialog.showModal();
    lockScroll();
    const focusTarget = dialog.querySelector('[autofocus]');
    if (focusTarget) focusTarget.focus();
    dialog.dispatchEvent(new CustomEvent('drawer:open'));
  };

  G.closeDrawer = (dialog) => {
    if (!dialog || !dialog.open) return;
    dialog.close();
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      G.openDrawer(opener.dataset.drawerOpen, opener);
      return;
    }
    const closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      G.closeDrawer(closer.closest('dialog'));
      return;
    }
    // Click on the backdrop (outside the dialog box) closes it.
    if (event.target instanceof HTMLDialogElement && event.target.classList.contains('drawer')) {
      const rect = event.target.getBoundingClientRect();
      const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) G.closeDrawer(event.target);
    }
  });

  document.addEventListener('close', (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement)) return;
    lockScroll();
    if (dialog._opener && document.contains(dialog._opener)) dialog._opener.focus();
  }, true);

  /* ---------- Mega menu (details/summary; hover on fine pointers) ---------- */
  const menus = () => document.querySelectorAll('[data-mega]');
  const closeMenus = (except) => menus().forEach((d) => { if (d !== except) d.removeAttribute('open'); });

  document.addEventListener('toggle', (event) => {
    const d = event.target;
    if (d.matches && d.matches('[data-mega]') && d.open) closeMenus(d);
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelector('[data-mega][open]');
    if (open) {
      open.removeAttribute('open');
      open.querySelector('summary').focus();
    }
  });

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-mega]')) closeMenus();
  });

  if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    let timer;
    // hover target = the whole menu item (category link + chevron + panel)
    const megaOf = (el) => el?.closest?.('[data-mega-item]')?.querySelector('[data-mega]') || el?.closest?.('[data-mega]');
    document.addEventListener('pointerover', (event) => {
      const d = megaOf(event.target);
      if (!d) return;
      clearTimeout(timer);
      timer = setTimeout(() => { if (!d.open) d.setAttribute('open', ''); }, 90);
    });
    document.addEventListener('pointerout', (event) => {
      const d = megaOf(event.target);
      if (!d || (d.closest('[data-mega-item]') || d).contains(event.relatedTarget)) return;
      clearTimeout(timer);
      timer = setTimeout(() => d.removeAttribute('open'), 160);
    });
  }

  /* ---------- Sticky header: hide on scroll down, show on scroll up ---------- */
  const header = document.querySelector('[data-sticky-header="smart"]');
  if (header) {
    let lastY = window.scrollY;
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = Math.max(0, window.scrollY);
        const delta = y - lastY;
        ticking = false;
        if (Math.abs(delta) < 6) return; // ignore touch jitter / rubber-banding
        const menuOpen = header.querySelector('[data-mega][open]');
        header.classList.toggle('is-hidden', !menuOpen && delta > 0 && y > header.offsetHeight * 2);
        lastY = y;
      });
    }, { passive: true });
    // Keyboard users tabbing into the header always get it back.
    header.addEventListener('focusin', () => header.classList.remove('is-hidden'));
  }

  /* ---------- Store hours: mark today ---------- */
  const today = ((new Date().getDay() + 6) % 7) + 1; // 1 = Monday … 7 = Sunday
  document.querySelectorAll('[data-store-hours] [data-days]').forEach((row) => {
    if (row.dataset.days.split(',').map(Number).includes(today)) {
      row.querySelectorAll('dt, dd').forEach((el) => el.setAttribute('data-today', ''));
    }
  });

  /* ---------- Quantity steppers ---------- */
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-qty-step]');
    if (!btn) return;
    const input = btn.closest('.qty').querySelector('input');
    const step = Number(btn.dataset.qtyStep);
    const min = Number(input.min || 0);
    const max = input.max ? Number(input.max) : Infinity;
    const next = Math.min(max, Math.max(min, (Number(input.value) || 0) + step));
    if (next === Number(input.value)) return;
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Toast ---------- */
  let toastTimer;
  G.toast = (message) => {
    let el = document.getElementById('gn-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'gn-toast';
      el.className = 'toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.textContent = message;
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 3200);
  };

  /* ---------- Tabs (WAI-ARIA tabs pattern; panels are all visible without JS) ---------- */
  if (!customElements.get('gn-tabs')) {
    customElements.define('gn-tabs', class extends HTMLElement {
      connectedCallback() {
        this.tabs = [...this.querySelectorAll('[role="tab"]')];
        this.tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => this.select(i));
          tab.addEventListener('keydown', (e) => {
            const map = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: this.tabs.length - 1 };
            if (!(e.key in map)) return;
            e.preventDefault();
            this.select((map[e.key] + this.tabs.length) % this.tabs.length, true);
          });
        });
      }
      select(index, focus = false) {
        this.tabs.forEach((tab, i) => {
          const selected = i === index;
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
          document.getElementById(tab.getAttribute('aria-controls')).hidden = !selected;
          if (selected && focus) tab.focus();
        });
      }
    });
  }

  /* ---------- Section Rendering helper ---------- */
  G.fetchSection = async (url, sectionId) => {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('section_id', sectionId);
    const res = await fetch(u, { headers: { Accept: 'text/html' } });
    if (!res.ok) throw new Error(res.statusText);
    return new DOMParser().parseFromString(await res.text(), 'text/html');
  };

  /* ---------- Video facade: swap in the youtube-nocookie player on click ---------- */
  document.addEventListener('click', (e) => {
    const a = e.target.closest('[data-yt-id]');
    if (!a || e.ctrlKey || e.metaKey || e.shiftKey) return;
    e.preventDefault();
    const frame = document.createElement('iframe');
    frame.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(a.dataset.ytId)}?autoplay=1&rel=0`;
    frame.title = a.getAttribute('aria-label') || 'YouTube';
    frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    frame.allowFullscreen = true;
    a.replaceWith(frame);
    frame.focus();
  });

  /* ---------- Back to top ---------- */
  const toTop = document.querySelector('[data-back-to-top]');
  if (toTop) {
    toTop.hidden = false;
    let ticking = false;
    const update = () => {
      toTop.classList.toggle('is-visible', window.scrollY > window.innerHeight * 1.5);
      ticking = false;
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    update();
    toTop.addEventListener('click', (e) => {
      e.preventDefault();
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
      document.getElementById('MainContent')?.focus({ preventScroll: true });
    });
  }

  /* ---------- Rail arrows (mobile product rails) ---------- */
  const syncRail = (rail) => {
    const nav = rail.closest('.container')?.querySelector('.rail-nav');
    if (!nav) return;
    const [prev, next] = nav.querySelectorAll('[data-rail-scroll]');
    prev.disabled = rail.scrollLeft < 8;
    next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 8;
  };
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-rail-scroll]');
    if (!btn) return;
    const rail = btn.closest('.container')?.querySelector('.rail');
    const item = rail?.firstElementChild;
    if (!item) return;
    const step = item.getBoundingClientRect().width + (parseFloat(getComputedStyle(rail).columnGap) || 0);
    rail.scrollBy({ left: step * Number(btn.dataset.railScroll), behavior: 'smooth' });
  });
  document.addEventListener('scroll', (e) => { if (e.target.classList?.contains('rail')) syncRail(e.target); }, { capture: true, passive: true });
  const syncAllRails = () => document.querySelectorAll('.rail-nav').forEach((n) => {
    const r = n.closest('.container')?.querySelector('.rail');
    if (r) syncRail(r);
  });
  // recommendations arrive later via fetch
  new MutationObserver(syncAllRails).observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
  syncAllRails();

  /* ---------- "Nastavení cookies" in the footer → Shopify cookie banner preferences ---------- */
  const cookieBtn = document.querySelector('[data-cookie-settings]');
  if (cookieBtn) {
    const ready = () => typeof window.privacyBanner?.showPreferences === 'function';
    const reveal = () => { if (ready()) cookieBtn.hidden = false; };
    reveal(); setTimeout(reveal, 1500); setTimeout(reveal, 4000);
    cookieBtn.addEventListener('click', () => { if (ready()) window.privacyBanner.showPreferences(); });
  }
})();
